// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/components/AttachmentField.tsx   (فایل جدید — مشترک)
//
// فیلد ضمیمه‌ی استاندارد برای کل سیستم: کلیک برای انتخاب فایل،
// Paste از کلیپ‌بورد، و یک دکمه‌ی ضبط صدا (میکروفون) که یادداشت
// صوتی را ضبط و آپلود می‌کند — دقیقاً مثل بقیه‌ی ضمیمه‌ها ذخیره و
// در گزارش‌ها قابل پخش/دانلود است.
// ============================================================

import { useState, useRef, useEffect } from "react";
import { Paperclip, Loader2, Mic, Square, X } from "lucide-react";

interface UploadResult { success: boolean; path?: string; file_name?: string; message?: string; }

interface Props {
  value: string;
  fileName?: string;
  onChange: (path: string, fileName: string) => void;
  uploadFn: (file: File) => Promise<UploadResult>;
  T: any;
  dark: boolean;
  placeholder?: string;
}

export default function AttachmentField({ value, fileName, onChange, uploadFn, T, dark, placeholder = "کلیک برای فایل، Paste یا ضبط صدا" }: Props) {
  const [uploading, setUploading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<any>(null);

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const doUpload = async (file: File) => {
    setUploading(true);
    const res = await uploadFn(file);
    if (res.success) onChange(res.path || "", res.file_name || file.name);
    setUploading(false);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const fileItem = Array.from(e.clipboardData.items).find(it => it.kind === "file");
    const file = fileItem?.getAsFile();
    if (file) { e.preventDefault(); doUpload(file); }
  };

  const startRecording = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = ev => { if (ev.data.size > 0) chunksRef.current.push(ev.data); };
      mr.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const file = new File([blob], `یادداشت-صوتی-${Date.now()}.webm`, { type: "audio/webm" });
        doUpload(file);
      };
      mediaRecorderRef.current = mr;
      mr.start();
      setRecording(true);
      setRecordSeconds(0);
      timerRef.current = setInterval(() => setRecordSeconds(s => s + 1), 1000);
    } catch {
      alert("دسترسی به میکروفون ممکن نشد. اجازه‌ی دسترسی را در مرورگر بررسی کنید.");
    }
  };

  const stopRecording = (e: React.MouseEvent) => {
    e.stopPropagation();
    mediaRecorderRef.current?.stop();
    setRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  };

  const clear = (e: React.MouseEvent) => { e.stopPropagation(); onChange("", ""); };

  const mm = String(Math.floor(recordSeconds / 60)).padStart(2, "0");
  const ss = String(recordSeconds % 60).padStart(2, "0");

  return (
    <div
      onPaste={handlePaste}
      tabIndex={0}
      style={{
        display: "flex", alignItems: "center", gap: 8, padding: "10px 12px", borderRadius: 10,
        border: `1.5px solid ${recording ? "#ef4444" : T.border}`, background: T.input,
        fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif", minHeight: 42, boxSizing: "border-box",
      }}
    >
      <button
        type="button"
        onClick={() => fileRef.current?.click()}
        title="انتخاب فایل"
        style={{ background: "none", border: "none", cursor: "pointer", color: T.subtext, display: "flex", padding: 0 }}
      >
        <Paperclip size={15} />
      </button>

      <div style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer", color: T.subtext }} onClick={() => fileRef.current?.click()}>
        {uploading ? (
          <span style={{ display: "flex", alignItems: "center", gap: 6 }}><Loader2 size={13} /> در حال آپلود...</span>
        ) : recording ? (
          <span style={{ color: "#ef4444", fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#ef4444", animation: "grPulse 1s infinite" }} /> در حال ضبط... {mm}:{ss}
          </span>
        ) : value ? (
          <span style={{ color: "#10b981", fontWeight: 600 }}>{fileName || "فایل ضمیمه شد"}</span>
        ) : (
          <span>{placeholder}</span>
        )}
      </div>

      {value && !recording && !uploading && (
        <button type="button" onClick={clear} title="حذف ضمیمه" style={{ background: "none", border: "none", cursor: "pointer", color: T.subtext, display: "flex", padding: 0 }}>
          <X size={14} />
        </button>
      )}

      <button
        type="button"
        onClick={recording ? stopRecording : startRecording}
        title={recording ? "پایان ضبط" : "ضبط یادداشت صوتی"}
        style={{
          background: recording ? "#ef4444" : (dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"),
          border: "none", borderRadius: 7, width: 26, height: 26, cursor: "pointer",
          color: recording ? "#fff" : T.subtext, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}
      >
        {recording ? <Square size={11} /> : <Mic size={13} />}
      </button>

      <input ref={fileRef} type="file" style={{ display: "none" }} onChange={e => { const f = e.target.files?.[0]; if (f) doUpload(f); e.target.value = ""; }} />
      <style>{`@keyframes grPulse { 0%,100% { opacity: 1; } 50% { opacity: 0.3; } }`}</style>
    </div>
  );
}
